import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { rouvyAnalyzerRouteUrl } from '../utils/rouvyAnalyzer';
import './RouvyAnalyzerModal.css';

// Прогноз времени на маршруте и лучшее снаряжение от стороннего Rouvy Analyzer.
// Браузер в анализатор не пускает CORS, поэтому данные идут через наш прокси:
// GET /api/rouvy_analyzer/:rouvy_id?power=&weight=&gender=&strategy=&drafting=
// (RouvyAnalyzerService)
const TOP_SIZE = 10;

const BIKE_TYPES = ['all', 'road', 'timetrial', 'gravel'];

// Стратегия раскладки мощности, как на сайте анализатора: ровная или по уклону
const STRATEGIES = ['constant', 'grade'];

const DEFAULT_STRATEGY = 'constant';
const DEFAULT_DRAFTING = '0';

const pad = (value) => String(value).padStart(2, '0');

const formatDuration = (seconds) => {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

const formatGap = (seconds) => (seconds > 0 ? `+${formatDuration(seconds)}` : '—');

// Вес в профиле хранится decimal и приходит строкой
const profileValue = (value) => (value === null || value === undefined ? '' : String(Number(value)));

// Держать FTP всю гонку нереально, поэтому прогноз по умолчанию считаем на 90%
// от него с округлением вниз. Та же доля на бэкенде — RouvyAnalyzerController
const RACE_POWER_SHARE = 0.9;

const racePower = (ftp) => (ftp ? String(Math.floor(Number(ftp) * RACE_POWER_SHARE)) : '');

// Последние отправленные параметры запоминаем в браузере отдельно для каждого
// участника и подставляем при следующем открытии вместо значений из профиля
const lastInputKey = (userId) => `rouvy_analyzer_last_input_${userId}`;

const loadLastInput = (userId) => {
  if (!userId) return null;

  try {
    const saved = JSON.parse(localStorage.getItem(lastInputKey(userId)));
    if (!saved || !saved.power || !saved.weight) return null;

    const drafting = Number(saved.drafting);

    return {
      power: String(saved.power),
      weight: String(saved.weight),
      gender: saved.gender === 'female' ? 'female' : 'male',
      // Сохраненные до появления стратегии и драфтинга параметры их не содержат
      strategy: STRATEGIES.includes(saved.strategy) ? saved.strategy : DEFAULT_STRATEGY,
      drafting: Number.isInteger(drafting) && drafting >= 0 && drafting <= 100 ? String(drafting) : DEFAULT_DRAFTING,
    };
  } catch (e) {
    return null;
  }
};

const saveLastInput = (userId, input) => {
  if (!userId) return;

  try {
    localStorage.setItem(lastInputKey(userId), JSON.stringify(input));
  } catch (e) {
    // Хранилище недоступно (приватный режим, квота) — просто не запоминаем
  }
};

// События из портала всплывают по дереву React к родителям компонента. Карточка
// маршрута живет в карусели (EventView), которая на mousedown делает
// preventDefault и тянет слайды: без этой преграды поля формы не получают фокус,
// а select не раскрывается
const stopBubbling = (e) => e.stopPropagation();

const RouvyAnalyzerModal = ({ isOpen, onClose, rouvyId, routeName }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  // Объект профиля пересоздается при каждом его обновлении — форму сбрасываем
  // только когда меняются сами значения
  const userFtp = user?.ftp;
  const userWeight = user?.weight;
  const userGender = user?.gender;
  const userId = user?.id;

  const [power, setPower] = useState('');
  const [weight, setWeight] = useState('');
  const [gender, setGender] = useState('male');
  const [strategy, setStrategy] = useState(DEFAULT_STRATEGY);
  const [drafting, setDrafting] = useState(DEFAULT_DRAFTING);
  const [bikeType, setBikeType] = useState('all');
  // Параметры последнего запроса: меняются только по кнопке расчета
  const [query, setQuery] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // На узком экране шесть колонок не помещаются — позиции идут карточками
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [error, setError] = useState(null);

  // При открытии форма заполняется последними введенными параметрами, а если их
  // еще не было — из профиля: мощность — 90% FTP. Когда мощность и вес есть,
  // прогноз считается сразу — иначе ждем, пока участник их введет
  useEffect(() => {
    if (!isOpen) return;

    const initial = loadLastInput(userId) || {
      power: racePower(userFtp),
      weight: profileValue(userWeight),
      gender: userGender === 'female' ? 'female' : 'male',
      strategy: DEFAULT_STRATEGY,
      drafting: DEFAULT_DRAFTING,
    };

    setPower(initial.power);
    setWeight(initial.weight);
    setGender(initial.gender);
    setStrategy(initial.strategy);
    setDrafting(initial.drafting);
    setBikeType('all');
    setData(null);
    setError(null);
    setQuery(initial.power && initial.weight ? initial : null);
  }, [isOpen, rouvyId, userId, userFtp, userWeight, userGender]);

  useEffect(() => {
    if (!isOpen || !rouvyId || !query) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`/api/rouvy_analyzer/${encodeURIComponent(rouvyId)}`, { params: query });
        if (!cancelled) setData(response.data);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(e.response?.data?.error || t('rouvy_analyzer.error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      setLoading(false);
    };
    // t намеренно не в зависимостях: функция перевода пересоздается на каждом
    // рендере провайдера и перезапускала бы расчет
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rouvyId, query]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);

    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const rows = useMemo(() => {
    const results = (data && data.results) || [];
    const filtered = bikeType === 'all' ? results : results.filter(row => row.bike.type === bikeType);

    return filtered.slice(0, TOP_SIZE);
  }, [data, bikeType]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    const input = { power, weight, gender, strategy, drafting };

    saveLastInput(userId, input);
    setQuery(input);
  };

  const route = data && data.route;
  const bestTime = rows.length ? rows[0].time : null;
  const analyzerUrl = rouvyAnalyzerRouteUrl(rouvyId);

  const speed = (time) => (
    route && time > 0 ? ((route.distance / 1000) / (time / 3600)).toFixed(1) : '—'
  );

  // Модели с той же аэродинамикой и весом едут одинаково — их перечисляем
  // в подсказке, а в таблице оставляем одну строку
  const equipmentCell = (item, badge = null) => (
    <div className="rouvy-analyzer-equipment" title={item.same.length ? item.same.join(', ') : undefined}>
      {item.image_url && (
        <img
          className="rouvy-analyzer-equipment-image"
          src={item.image_url}
          alt=""
          // Картинки лежат на CDN ROUVY: не загрузилась — просто без нее
          onError={(e) => { e.currentTarget.hidden = true; }}
        />
      )}
      <div>
        <div className="rouvy-analyzer-equipment-name">
          {item.name}
          {badge && <span className="rouvy-analyzer-type">{badge}</span>}
        </div>
        {item.same.length > 0 && (
          <div className="rouvy-analyzer-equipment-same">
            {t('rouvy_analyzer.same_models').replace('{{count}}', item.same.length)}
          </div>
        )}
      </div>
    </div>
  );

  const renderForm = () => (
    <form className="rouvy-analyzer-form" onSubmit={handleSubmit}>
      <label className="rouvy-analyzer-field">
        <span>{t('rouvy_analyzer.power')}</span>
        <input
          type="number"
          min="50"
          max="1500"
          step="1"
          required
          value={power}
          onChange={(e) => setPower(e.target.value)}
        />
      </label>
      <label className="rouvy-analyzer-field">
        <span>{t('rouvy_analyzer.weight')}</span>
        <input
          type="number"
          min="30"
          max="200"
          step="0.1"
          required
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
      </label>
      <label className="rouvy-analyzer-field">
        <span>{t('rouvy_analyzer.gender')}</span>
        <select value={gender} onChange={(e) => setGender(e.target.value)}>
          <option value="male">{t('rouvy_analyzer.gender_male')}</option>
          <option value="female">{t('rouvy_analyzer.gender_female')}</option>
        </select>
      </label>
      <label className="rouvy-analyzer-field rouvy-analyzer-field-wide" title={t(`rouvy_analyzer.strategy_${strategy}_hint`)}>
        <span>{t('rouvy_analyzer.strategy')}</span>
        <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
          {STRATEGIES.map(value => (
            <option key={value} value={value}>{t(`rouvy_analyzer.strategy_${value}`)}</option>
          ))}
        </select>
      </label>
      <label className="rouvy-analyzer-field" title={t('rouvy_analyzer.drafting_hint')}>
        <span>{t('rouvy_analyzer.drafting')}</span>
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          required
          value={drafting}
          onChange={(e) => setDrafting(e.target.value)}
        />
      </label>
      <button type="submit" className="rouvy-analyzer-submit" disabled={loading}>
        {t('rouvy_analyzer.calculate')}
      </button>
    </form>
  );

  const renderResults = () => {
    if (loading) {
      return (
        <div className="rouvy-analyzer-state">
          <div>{t('rouvy_analyzer.loading')}</div>
          <div className="rouvy-analyzer-hint">{t('rouvy_analyzer.loading_hint')}</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="rouvy-analyzer-state rouvy-analyzer-error">
          {error}
          {query && (
            <div>
              <button type="button" className="rouvy-analyzer-retry" onClick={() => setQuery({ ...query })}>
                {t('rouvy_analyzer.retry')}
              </button>
            </div>
          )}
        </div>
      );
    }

    if (!data) {
      return <div className="rouvy-analyzer-state">{t('rouvy_analyzer.enter_params')}</div>;
    }

    return (
      <>
        <div className="rouvy-analyzer-summary">
          <span>{route.name}</span>
          <span>{(route.distance / 1000).toFixed(1)} {t('rouvy_analyzer.km')}</span>
          <span>↗ {Math.round(route.ascent)} {t('rouvy_analyzer.m')}</span>
          <span>
            {data.input.power} {t('rouvy_analyzer.watts')} · {data.input.weight} {t('rouvy_analyzer.kg')}
          </span>
          <span>
            {t(`rouvy_analyzer.strategy_${data.input.strategy}`)} · {t('rouvy_analyzer.drafting_short')} {data.input.drafting}%
          </span>
        </div>

        <div className="rouvy-analyzer-tabs">
          {BIKE_TYPES.map(type => (
            <button
              key={type}
              type="button"
              className={`rouvy-analyzer-tab ${bikeType === type ? 'rouvy-analyzer-tab-active' : ''}`}
              onClick={() => setBikeType(type)}
            >
              {t(`rouvy_analyzer.bike_type_${type}`)}
            </button>
          ))}
        </div>

        {rows.length === 0 && (
          <div className="rouvy-analyzer-state">{t('rouvy_analyzer.empty')}</div>
        )}

        {/* Карточка: рама, колеса, затем время, отставание и скорость */}
        {rows.length > 0 && isMobile && (
          <ol className="rouvy-analyzer-cards">
            {rows.map((row, index) => (
              <li
                key={`${row.bike.name}-${row.wheel.name}`}
                className={`rouvy-analyzer-card ${index === 0 ? 'rouvy-analyzer-card-best' : ''}`}
              >
                <div className="rouvy-analyzer-card-row">
                  <span className="rouvy-analyzer-card-rank">{index + 1}</span>
                  {equipmentCell(row.bike, t(`rouvy_analyzer.bike_type_${row.bike.type}`))}
                </div>
                <div className="rouvy-analyzer-card-row">
                  <span className="rouvy-analyzer-card-rank" aria-hidden="true" />
                  {equipmentCell(row.wheel)}
                </div>
                <div className="rouvy-analyzer-card-stats">
                  <div>
                    <span className="rouvy-analyzer-card-label">{t('rouvy_analyzer.time')}</span>
                    <span className="rouvy-analyzer-cell-time">{formatDuration(row.time)}</span>
                  </div>
                  <div>
                    <span className="rouvy-analyzer-card-label">{t('rouvy_analyzer.gap')}</span>
                    <span className="rouvy-analyzer-cell-muted">{formatGap(row.time - bestTime)}</span>
                  </div>
                  <div>
                    <span className="rouvy-analyzer-card-label">{t('rouvy_analyzer.speed')}</span>
                    <span className="rouvy-analyzer-cell-muted">{speed(row.time)} {t('rouvy_analyzer.kmh')}</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}

        {rows.length > 0 && !isMobile && (
          <div className="rouvy-analyzer-table-wrap">
            <table className="rouvy-analyzer-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('rouvy_analyzer.bike')}</th>
                  <th>{t('rouvy_analyzer.wheels')}</th>
                  <th>{t('rouvy_analyzer.time')}</th>
                  <th>{t('rouvy_analyzer.gap')}</th>
                  <th>{t('rouvy_analyzer.speed')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.bike.name}-${row.wheel.name}`}>
                    <td className="rouvy-analyzer-cell-rank">{index + 1}</td>
                    <td>{equipmentCell(row.bike, t(`rouvy_analyzer.bike_type_${row.bike.type}`))}</td>
                    <td>{equipmentCell(row.wheel)}</td>
                    <td className="rouvy-analyzer-cell-time">{formatDuration(row.time)}</td>
                    <td className="rouvy-analyzer-cell-muted">{formatGap(row.time - bestTime)}</td>
                    <td className="rouvy-analyzer-cell-muted">{speed(row.time)} {t('rouvy_analyzer.kmh')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="rouvy-analyzer-hint">{t('rouvy_analyzer.disclaimer')}</p>
      </>
    );
  };

  return ReactDOM.createPortal(
    // Окно открывается и из карточки маршрута, которая целиком ссылка, и поверх
    // разбора гонки: клики дальше модалки не пропускаем
    <div
      className="rouvy-analyzer-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onMouseDown={stopBubbling}
      onMouseMove={stopBubbling}
      onMouseUp={stopBubbling}
      onTouchStart={stopBubbling}
      onTouchMove={stopBubbling}
      onTouchEnd={stopBubbling}
      onKeyDown={stopBubbling}
      onKeyPress={stopBubbling}
    >
      <div className="rouvy-analyzer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rouvy-analyzer-header">
          <div>
            <h3 className="rouvy-analyzer-title">{t('rouvy_analyzer.title')}</h3>
            <div className="rouvy-analyzer-subtitle">
              {routeName}
              {analyzerUrl && (
                <a
                  className="rouvy-analyzer-link"
                  href={analyzerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('rouvy_analyzer.open_site_hint')}
                >
                  {t('rouvy_analyzer.open_site')} ↗
                </a>
              )}
            </div>
          </div>
          <button type="button" className="rouvy-analyzer-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="rouvy-analyzer-body">
          {/* Расчет идет через наш API и доступен только после входа. Гостю форму
              не показываем: 401 от API разлогинивает и уводит на страницу входа */}
          {user ? (
            <>
              {renderForm()}
              {renderResults()}
            </>
          ) : (
            <div className="rouvy-analyzer-state">{t('rouvy_analyzer.login_required')}</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RouvyAnalyzerModal;
